import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Check, X, Sparkles, Crown, Mail } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocalizedPrices } from "@/hooks/useLocalizedPrices";
import logo from "@/assets/logo.webp";

export const Route = createFileRoute("/pricing")({
  component: PricingPage,
  head: () => ({
    meta: [
      { title: "Pricing — Business Dite Ads" },
      {
        name: "description",
        content:
          "Choose the plan that fits your business: Starter $29, Pro $59, Business $159, or Enterprise. 14-day free trial. Cancel anytime.",
      },
      { property: "og:title", content: "Pricing — Business Dite Ads" },
      { property: "og:url", content: "https://busditeads.com/pricing" },
      {
        property: "og:description",
        content:
          "Four plans for every team size — Starter, Pro, Business, Enterprise. Start with a 14-day free trial.",
      },
    ],
    links: [{ rel: "canonical", href: "https://busditeads.com/pricing" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: (
            (i18n.t("pricing.faqs", { returnObjects: true }) as Array<{ q: string; a: string }>) ??
            []
          ).map((faq) => ({
            "@type": "Question",
            name: faq.q,
            acceptedAnswer: { "@type": "Answer", text: faq.a },
          })),
        }),
      },
    ],
  }),
});

type PlanKey = "starter" | "pro" | "business" | "enterprise";

interface PlanDef {
  key: PlanKey;
  priceId?: string;
  fallback: string;
  highlight?: boolean;
  custom?: boolean;
  hasPremium: boolean;
}

const PLANS: PlanDef[] = [
  { key: "starter", priceId: "starter_monthly", fallback: "$29", hasPremium: false },
  { key: "pro", priceId: "pro_monthly", fallback: "$59", highlight: true, hasPremium: true },
  { key: "business", priceId: "business_monthly", fallback: "$159", hasPremium: true },
  { key: "enterprise", priceId: "enterprise_monthly", fallback: "$350", hasPremium: true },
];

const PRICE_IDS = PLANS.filter((p) => p.priceId).map((p) => p.priceId!) as string[];
const FALLBACKS: Record<string, string> = PLANS.reduce(
  (acc, p) => {
    if (p.priceId) acc[p.priceId] = p.fallback;
    return acc;
  },
  {} as Record<string, string>,
);

function PricingPage() {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const { prices } = useLocalizedPrices(PRICE_IDS, FALLBACKS);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="Business Dite Ads" className="h-9 w-9 object-contain" />
            <span className="text-base font-semibold tracking-tight">
              Business <span className="text-primary">Dite Ads</span>
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Button
              asChild
              size="sm"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Link to="/auth">{mounted ? t("landing.signIn") : "Sign in"}</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
        <div className="text-center">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            {t("pricing.badge")}
          </div>
          <h1
            className="bg-clip-text text-3xl font-bold leading-tight tracking-tight text-transparent sm:text-5xl"
            style={{ backgroundImage: "var(--gradient-brand)" }}
          >
            {t("pricing.title")}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-pretty text-sm text-muted-foreground sm:text-base">
            {t("pricing.subtitle")}
          </p>
        </div>

        <div className="mx-auto mt-12 grid max-w-7xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((plan) => (
            <PlanCard
              key={plan.key}
              plan={plan}
              displayPrice={
                plan.priceId ? (prices[plan.priceId]?.formatted ?? plan.fallback) : plan.fallback
              }
            />
          ))}
        </div>

        <section className="mx-auto mt-20 max-w-3xl">
          <h2 className="text-center text-2xl font-bold tracking-tight">{t("pricing.faqTitle")}</h2>
          <div className="mt-6 space-y-4">
            {(t("pricing.faqs", { returnObjects: true }) as Array<{ q: string; a: string }>).map(
              (faq) => (
                <Card key={faq.q} className="p-5">
                  <h3 className="text-sm font-semibold">{faq.q}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground">{faq.a}</p>
                </Card>
              ),
            )}
          </div>
          <p className="mt-6 text-center text-xs text-muted-foreground">
            {t("pricing.refundLink")}{" "}
            <Link to="/refund" className="text-primary hover:underline">
              {t("legal.refund.title")}
            </Link>
            .
          </p>
        </section>
      </main>

      <footer className="border-t border-border/60 py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 text-xs text-muted-foreground sm:flex-row sm:px-6">
          <div className="flex items-center gap-2">
            <img src={logo} alt="" className="h-5 w-5 object-contain" />
            <span>© {new Date().getFullYear()} Business Dite Ads</span>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/privacy" className="hover:text-foreground">
              {t("landing.privacy")}
            </Link>
            <Link to="/terms" className="hover:text-foreground">
              {t("landing.terms")}
            </Link>
            <Link to="/refund" className="hover:text-foreground">
              {t("legal.refund.short")}
            </Link>
            <a href="mailto:contact@diteads.com" className="hover:text-foreground">
              {t("landing.contact")}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function PlanCard({ plan, displayPrice }: { plan: PlanDef; displayPrice: string }) {
  const { t } = useTranslation();
  const featuresRaw = t(`pricing.plans.${plan.key}.features`, { returnObjects: true });
  const features: string[] = Array.isArray(featuresRaw) ? (featuresRaw as string[]) : [];
  const exclusionsRaw =
    plan.key === "starter" ? t(`pricing.plans.starter.excluded`, { returnObjects: true }) : [];
  const exclusions: string[] = Array.isArray(exclusionsRaw) ? (exclusionsRaw as string[]) : [];

  return (
    <Card
      className={cn(
        "relative flex flex-col overflow-hidden p-6",
        plan.highlight && "border-primary/40 shadow-[var(--shadow-glow)]",
      )}
    >
      {plan.highlight && (
        <div className="absolute right-3 top-3 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
          {t("pricing.mostPopular")}
        </div>
      )}
      {plan.key === "enterprise" && (
        <div className="absolute right-3 top-3 rounded-full bg-amber-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
          <Crown className="inline h-3 w-3 -mt-0.5 mr-0.5" />
          {t("pricing.enterprise")}
        </div>
      )}

      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-primary">
          {t(`pricing.plans.${plan.key}.name`)}
        </p>
        <div className="mt-3 flex items-baseline gap-1">
          <span className="text-4xl font-bold tracking-tight">{displayPrice}</span>
          {!plan.custom && (
            <span className="text-sm text-muted-foreground">/{t("pricing.month")}</span>
          )}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {t(`pricing.plans.${plan.key}.tagline`)}
        </p>
      </div>

      <ul className="mt-5 flex-1 space-y-2">
        {features.map((label) => (
          <li key={label} className="flex items-start gap-2 text-sm">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>{label}</span>
          </li>
        ))}
        {exclusions.map((label) => (
          <li
            key={label}
            className="flex items-start gap-2 text-sm text-muted-foreground line-through"
          >
            <X className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{label}</span>
          </li>
        ))}
      </ul>

      {plan.custom ? (
        <Button asChild size="lg" variant="outline" className="mt-6 w-full gap-2">
          <a href="mailto:contact@diteads.com?subject=Enterprise%20Plan%20Inquiry">
            <Mail className="h-4 w-4" />
            {t("pricing.contactSales")}
          </a>
        </Button>
      ) : (
        <Button
          asChild
          size="lg"
          className={cn(
            "mt-6 w-full",
            plan.highlight && "bg-primary text-primary-foreground hover:bg-primary/90",
          )}
          variant={plan.highlight ? "default" : "outline"}
        >
          <Link to="/auth">{t("pricing.startTrial")}</Link>
        </Button>
      )}

      {!plan.custom && (
        <p className="mt-2 text-center text-[11px] text-muted-foreground">{t("pricing.noCard")}</p>
      )}
    </Card>
  );
}
